// Small live lists shared by several pages: school breaks, top drivers and the sponsor.
// Same data and rules as Breaks.swift and TopDriver.swift on iOS.

import { useEffect, useState } from 'react';
import { getDatabase, ref, onValue, get, query, orderByChild, equalTo } from 'firebase/database';
import { departure, localDay } from './places';

/* ── School breaks (config/breaks/{id} = { name, start, end, theme? }) ── */

const THEMES = {
  thanksgiving: { icon: '🍂', accent: '#d9661a', wash: 'linear-gradient(135deg, rgba(255,165,0,.18), rgba(153,64,26,.10))', tagline: 'Get home for turkey. Grab a seat early.' },
  winter:       { icon: '❄️', accent: '#338ce6', wash: 'linear-gradient(135deg, rgba(0,200,255,.14), rgba(37,99,235,.12))', tagline: 'Home for the holidays. Rides fill fast.' },
  spring:       { icon: '☀️', accent: '#0da699', wash: 'linear-gradient(135deg, rgba(250,204,21,.16), rgba(20,184,166,.14))', tagline: "Sun's out. Lock in your airport ride." },
  summer:       { icon: '🏖️', accent: '#f28c1a', wash: 'linear-gradient(135deg, rgba(250,204,21,.18), rgba(249,115,22,.14))', tagline: "Summer's here. Find your ride out." },
  graduation:   { icon: '🎓', accent: '#1a4dbf', wash: 'linear-gradient(135deg, rgba(37,99,235,.12), rgba(250,204,21,.14))', tagline: 'You made it. Get everyone where they need to be.' },
  patriotic:    { icon: '🇺🇸', accent: '#cc2633', wash: 'linear-gradient(135deg, rgba(220,38,38,.10), rgba(37,99,235,.12))', tagline: 'Long weekend. Get out of the Zoo while you can.' },
  longWeekend:  { icon: '🚗', accent: '#5966d9', wash: 'linear-gradient(135deg, rgba(99,102,241,.12), rgba(249,115,22,.10))', tagline: 'Three days off. Where are you headed?' },
  standard:     { icon: '📅', accent: '#2563eb', wash: 'linear-gradient(135deg, rgba(37,99,235,.09), rgba(37,99,235,.04))', tagline: 'Rides for the break, soonest first.' },
};

/** Picked by `theme` in the config, or guessed from the break's name and month (as on iOS). */
function themeFor(named, name, startMonth) {
  if (named && THEMES[named]) return named;
  const n = name.toLowerCase();
  const has = words => words.some(w => n.includes(w));
  if (has(['memorial', 'veterans', 'labor', 'president', 'independence', '4th', 'fourth'])) return 'patriotic';
  if (has(['thanksgiving', 'fall'])) return 'thanksgiving';
  if (has(['winter', 'christmas', 'holiday'])) return 'winter';
  if (n.includes('spring')) return 'spring';
  if (n.includes('summer')) return 'summer';
  if (has(['graduation', 'grad'])) return 'graduation';
  if (has(['weekend', 'columbus', 'mlk', 'king', 'indigenous', 'juneteenth'])) return 'longWeekend';
  if (startMonth === 11) return 'thanksgiving';
  if (startMonth === 12 || startMonth === 1) return 'winter';
  if (startMonth === 3 || startMonth === 4) return 'spring';
  if (startMonth >= 6 && startMonth <= 8) return 'summer';
  return 'standard';
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayIndex = ymd => Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10)) / 86400000;
const shortDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

function makeBreak(id, b) {
  if (!b || !b.name || !DAY.test(b.start || '') || !DAY.test(b.end || '') || b.start > b.end) return null;
  const theme = THEMES[themeFor(b.theme, b.name, +b.start.slice(5, 7))];
  const length = dayIndex(b.end) - dayIndex(b.start);
  const s = new Date(dayIndex(b.start) * 86400000);
  const e = new Date(dayIndex(b.end) * 86400000);
  const sameMonth = b.start.slice(0, 7) === b.end.slice(0, 7);
  const range = b.start === b.end ? shortDay.format(s)
    : `${shortDay.format(s)} – ${sameMonth ? e.getUTCDate() : shortDay.format(e)}`;
  return {
    id, name: b.name, start: b.start, end: b.end, range, ...theme,
    // Shown from 6 weeks before a real break, 1 week before a long weekend (≤ 4 days).
    leadDays: length < 4 ? 7 : 42,
    /** Whether a ride (or request) leaves on one of the break's days (Mountain Time). */
    contains(post) {
      const dep = departure(post);
      if (!dep) return false;
      const d = localDay(dep);
      return d >= b.start && d <= b.end;
    },
  };
}

/** The soonest break whose lead window covers today, or null. */
export function useCurrentBreak() {
  const [breaks, setBreaks] = useState([]);
  useEffect(() => onValue(ref(getDatabase(), 'config/breaks'), snap => {
    const list = [];
    snap.forEach(c => { const b = makeBreak(c.key, c.val()); if (b) list.push(b); });
    setBreaks(list.sort((a, b) => a.start.localeCompare(b.start)));
  }, () => setBreaks([])), []);

  const today = localDay(new Date());
  return breaks.find(b => {
    const showFrom = new Date((dayIndex(b.start) - b.leadDays) * 86400000).toISOString().slice(0, 10);
    return today >= showFrom && today <= b.end;
  }) || null;
}

/* ── Top drivers (topDrivers/{uid} = { rides, riders }, refreshed daily) ── */

export function useTopDrivers() {
  const [drivers, setDrivers] = useState({});
  useEffect(() => onValue(ref(getDatabase(), 'topDrivers'), snap => setDrivers(snap.val() || {}), () => setDrivers({})), []);
  return drivers;
}

/** A driver's public { sum, total, tags } from ratingSummary, or null. */
export function useRatingSummary(uid) {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!uid) return undefined;
    return onValue(ref(getDatabase(), `ratingSummary/${uid}`), snap => setSummary(snap.val()), () => setSummary(null));
  }, [uid]);
  return summary;
}

/**
 * Lifetime trips, counted from bookings like iOS MyProfile.fetchStats, so they
 * survive posts being deleted. A reservation: the provider drove, the booker
 * rode. A request: the booker drove, the provider rode. Each trip counts once
 * however many seats it had, and booking your own post counts for nothing.
 * Null until loaded.
 */
export function useTripStats(uid) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    if (!uid) return undefined;
    let live = true;
    const db = getDatabase();
    const bookingsBy = field => get(query(ref(db, 'bookings'), orderByChild(field), equalTo(uid)))
      .then(s => Object.values(s.val() || {}));
    Promise.all([bookingsBy('providerUserID'), bookingsBy('bookerUserID')]).then(([provided, booked]) => {
      const given = new Set();
      const taken = new Set();
      provided.filter(b => b.bookerUserID !== uid).forEach(b => (b.type === 'request' ? taken : given).add(b.rideID));
      booked.filter(b => b.providerUserID !== uid).forEach(b => (b.type === 'request' ? given : taken).add(b.rideID));
      if (live) setStats({ given: given.size, taken: taken.size });
    }).catch(() => { if (live) setStats({ given: 0, taken: 0 }); });
    return () => { live = false; };
  }, [uid]);
  return stats;
}

/* ── Sponsor (config/sponsor = { active, name, headline, code, url, imageURL, wideImageURL,
   logoURL, markURL, rideLabel, accentColor }) ──
   Set with scripts/sponsor.js; null when it's switched off. Sponsored free
   rides are rideReserve rides with `sponsor` set (scripts/sponsored_ride.js). */

export function useSponsor() {
  const [sponsor, setSponsor] = useState(null);
  useEffect(() => onValue(ref(getDatabase(), 'config/sponsor'), snap => {
    const s = snap.val();
    setSponsor(s && s.active === true && s.name ? s : null);
  }, () => setSponsor(null)), []);
  return sponsor;
}

/** How sponsored rides look, from config/sponsor, whether or not the ad is on. */
export function useSponsorTheme() {
  const [theme, setTheme] = useState(null);
  useEffect(() => onValue(ref(getDatabase(), 'config/sponsor'), snap => setTheme(snap.val()), () => setTheme(null)), []);
  return theme;
}

const matches = (theme, name) => theme?.name?.toLowerCase() === (name || '').toLowerCase();

/** The small mark for a sponsored ride, or null if there isn't one. */
export function sponsorMarkURL(theme, name) {
  if (matches(theme, name) && theme.markURL) return theme.markURL;
  return /wild\s*blue/i.test(name || '') ? `${process.env.PUBLIC_URL}/sponsors/wildblue-mark.png` : null;
}

export const sponsorRideLabel = (theme, name) => (matches(theme, name) && theme.rideLabel) || 'Sponsored Ride';

/** A CSS style setting the booking sheet's --tone when the sponsor has its own color. */
export const sponsorAccentStyle = (theme, name) =>
  (matches(theme, name) && /^#?[0-9a-f]{6}$/i.test(theme.accentColor || '')
    ? { '--tone': `#${theme.accentColor.replace('#', '')}` } : undefined);

/** A sponsor's logo: the live image, then the bundled one, then its name. */
export function SponsorLogo({ name, height = 20 }) {
  const theme = useSponsorTheme();
  if (matches(theme, name) && theme.logoURL) {
    return <img className="sponsor-logo" src={theme.logoURL} alt={name} style={{ height, width: 'auto', display: 'block' }} />;
  }
  if (/wild\s*blue/i.test(name || '')) {
    return <img className="sponsor-logo" src={`${process.env.PUBLIC_URL}/sponsors/wildblue-wordmark.png`}
      alt={name} style={{ height, width: 'auto', display: 'block' }} />;
  }
  return <strong className="sponsor-logo" style={{ color: '#185FA5' }}>{name}</strong>;
}
